import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import BaseDialog from '../../../BaseDialog'
import useStyles from './styles'
import { useDataApi } from '../../../../context/DataApiContext'

import WizardHeader from '../../../WizardHeader'
import ImportFileStep from './steps/ImportFileStep'
import PreviewFileStep from './steps/PreviewFileStep'
import ResultStep from './steps/ResultStep'
import workify from '../../../../utils/workify'
const worker = workify(new Worker(new URL('./file.worker.js', import.meta.url)))

// Maps backend field names to the workbook row object keys used in the datagrid.
// Must stay in sync with the mapping in PreviewFileStep/index.js (prepareAndSubmit).
const fieldToColumnKeyMap = {
  vehicleType: 'typeVehicule',
  operator: 'operateur',
  depot: 'depot',
  member: 'beneficiaire',
  location: 'lieu',
}

const Steps = {
  importFile: 0,
  previewFile: 1,
  result: 2,
}

const Actions = {
  CHANGE_STEP: 'CHANGE_STEP',
  IMPORT_FILE_START: 'IMPORT_FILE_START',
  IMPORTED_FILE: 'IMPORTED_FILE',
  ERROR_UPLOADING_FILE: 'ERROR_UPLOADING_FILE',
  SUCCESSFULLY_UPLOADED_FILE: 'SUCCESSFULLY_UPLOADED_FILE',
  ERROR_IMPORTING_FILE: 'ERROR_IMPORTING_FILE',
  LOADING_FINAL_STEP: 'LOADING_FINAL_STEP',
  UPDATE_FINAL_STEP: 'UPDATE_FINAL_STEP',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
}

const INITIAL_STATE = {
  step: Steps.importFile,
  workbook: {
    data: null,
    images: [],
    fileName: null,
  },
  loading: null,
  details: {},
  importingFile: false,
  validationErrors: [], // Array of validation errors from backend
  corrections: [], // Array of fuzzy corrections from backend
}

function reducer(state, payload) {
  switch (payload.action) {
    case Actions.CHANGE_STEP:
      return {
        ...state,
        step: payload.data,
      }
    case Actions.IMPORTED_FILE: {
      const isEmpty = workbookIsEmpty(payload.data)
      const incomingCorrections = payload.data.corrections || []
      const autoCorrections = incomingCorrections.filter(
        (c) => c.type === 'auto'
      )

      // Apply auto-corrections to workbook data so grid shows corrected values
      let workbookData = payload.data.data
      if (autoCorrections.length > 0 && workbookData) {
        workbookData = workbookData.map((row, i) => {
          const rowNumber = i + 2
          const rowAutoCorrections = autoCorrections.filter(
            (c) => c.row === rowNumber
          )
          if (rowAutoCorrections.length === 0) return row
          const updatedRow = { ...row }
          rowAutoCorrections.forEach((c) => {
            const colKey = fieldToColumnKeyMap[c.field]
            if (colKey) updatedRow[colKey] = c.correctedValue
          })
          return updatedRow
        })
      }

      return {
        ...state,
        step: isEmpty ? state.step : state.step + 1,
        workbook: { data: workbookData, fileName: payload.data.fileName },
        importingFile: false,
        validationErrors: payload.data.validationErrors || [],
        corrections: incomingCorrections,
      }
    }
    case Actions.IMPORT_FILE_START:
      return {
        ...state,
        importingFile: true,
      }
    case Actions.SUCCESSFULLY_UPLOADED_FILE:
      return {
        ...state,
        loadingMessage: null,
        rows: payload.data.rows,
        dStateId: payload.data.dStateId,
        error: payload.data.error,
        success: payload.data.success || !payload.data.dStateId, // Success if no dStateId means all rows were skipped
        message: payload.data.message,
      }
    case Actions.LOADING_FINAL_STEP:
      return {
        ...state,
        step: Steps.result, // Explicitly set to result step
        loadingMessage: payload.data.loadingMessage,
        error: payload.data.error,
      }
    case Actions.UPDATE_FINAL_STEP:
      return {
        ...state,
        loadingMessage: payload.data.loadingMessage,
        error: payload.data.error,
      }
    case Actions.ERROR_UPLOADING_FILE:
      return {
        ...state,
        loadingMessage: null,
        error: payload.data.error,
      }
    case Actions.VALIDATION_ERROR: {
      const incomingCorrections = payload.data.corrections || []
      const autoCorrections = incomingCorrections.filter(
        (c) => c.type === 'auto'
      )

      // Apply auto-corrections to a copy of the workbook data so the grid
      // shows the corrected values immediately (cells highlighted orange).
      let updatedWorkbookData = state.workbook.data
      if (autoCorrections.length > 0 && state.workbook.data) {
        updatedWorkbookData = state.workbook.data.map((row, i) => {
          const rowNumber = i + 2 // same convention as backend
          const rowAutoCorrections = autoCorrections.filter(
            (c) => c.row === rowNumber
          )
          if (rowAutoCorrections.length === 0) return row
          const updatedRow = { ...row }
          rowAutoCorrections.forEach((c) => {
            const colKey = fieldToColumnKeyMap[c.field]
            if (colKey) updatedRow[colKey] = c.correctedValue
          })
          return updatedRow
        })
      }

      return {
        ...state,
        step: Steps.previewFile,
        validationErrors: payload.data.errors || [],
        corrections: incomingCorrections,
        workbook: { ...state.workbook, data: updatedWorkbookData },
        loadingMessage: null,
      }
    }
    default:
      return state
  }
}

function workbookIsEmpty(workbook) {
  return workbook.data && workbook.data.length <= 0
}

function mapWorkbookToChassisData(rows) {
  return (rows || []).map((row) => ({
    nChassis: row.nChassis,
    nParc: row.nParc,
    vehicleType: row.typeVehicule,
    operator: row.operateur,
    member: row.beneficiaire,
    depot: row.depot,
    location: row.lieu,
  }))
}

export default function CreateChassisBulkDialog({
  open = false,
  onClose = () => {},
  onCreated = () => {},
}) {
  const { t } = useTranslation()
  const classes = useStyles()
  const { dataProvider } = useDataApi()
  const [wizardState, setWizardState] = useState(INITIAL_STATE)

  const onCloseDialog = () => {
    setWizardState(INITIAL_STATE)
    onClose()
  }

  const onStepClick = (step) => {
    setWizardState((state) =>
      reducer(state, {
        action: Actions.CHANGE_STEP,
        data: step,
      })
    )
  }

  const validateAndDispatchImportedFile = useCallback(
    async (sheetData, fileName) => {
      let validationErrors = []
      let corrections = []
      const chassisData = mapWorkbookToChassisData(sheetData)
      if (chassisData.length > 0) {
        try {
          const result = await dataProvider.chassis.validateBulkUpload(
            chassisData
          )
          validationErrors = result.errors || []
          corrections = result.corrections || []
        } catch (err) {
          console.error('Step-2 validation failed:', err)
        }
      }
      setWizardState((state) =>
        reducer(state, {
          action: Actions.IMPORTED_FILE,
          data: { data: sheetData, fileName, validationErrors, corrections },
        })
      )
    },
    [dataProvider]
  )

  const onImportFileStepConfirm = useCallback(
    async (didChange, file) => {
      let { workbook } = wizardState
      if (didChange) {
        setWizardState((state) =>
          reducer(state, {
            action: Actions.IMPORT_FILE_START,
          })
        )
        worker.performAction(
          {
            action: 'processImportedFile',
            args: [file],
          },
          async (result) => {
            const { sheetData, fileName } = result
            await validateAndDispatchImportedFile(sheetData, fileName)
          }
        )
      } else {
        await validateAndDispatchImportedFile(workbook.data, workbook.fileName)
      }
    },
    [wizardState.workbook, validateAndDispatchImportedFile]
  )

  const createImportFileStep = useCallback(() => {
    return (
      <ImportFileStep
        selectedFileName={wizardState.workbook.fileName}
        emptyFile={workbookIsEmpty(wizardState.workbook)}
        onConfirm={onImportFileStepConfirm}
        importingFile={wizardState.importingFile}
      />
    )
  }, [
    wizardState.workbook.fileName,
    wizardState.workbook.data,
    wizardState.importingFile,
    onImportFileStepConfirm,
  ])

  const onFileImportClick = useCallback(
    async (rows) => {
      setWizardState((state) =>
        reducer(state, {
          action: Actions.LOADING_FINAL_STEP,
          data: {
            error: null,
            loadingMessage: t(
              'create-chassis-dialog.bulk-upload-wizard.results-uploading-file-data'
            ),
          },
        })
      )

      dataProvider.cache.reset()

      try {
        // Re-validate with the current canvas data (user may have edited cells)
        const validationResponse =
          await dataProvider.chassis.validateBulkUpload(rows)

        if (validationResponse.errors && validationResponse.errors.length > 0) {
          setWizardState((state) =>
            reducer(state, {
              action: Actions.VALIDATION_ERROR,
              data: {
                errors: validationResponse.errors,
                corrections: validationResponse.corrections || [],
              },
            })
          )
          return
        }

        // Check if all rows were skipped (already exist)
        if (validationResponse.validatedRows.length === 0) {
          const message =
            validationResponse.skippedRows &&
            validationResponse.skippedRows.length > 0
              ? `Tous les châssis existent déjà. ${validationResponse.skippedRows.length} ligne(s) ignorée(s).`
              : 'Aucune ligne à importer'

          setWizardState((state) =>
            reducer(state, {
              action: Actions.LOADING_FINAL_STEP, // First go to the result step
              data: {
                error: null,
                loadingMessage: null,
              },
            })
          )

          // Then immediately mark as successful
          setTimeout(() => {
            setWizardState((state) =>
              reducer(state, {
                action: Actions.SUCCESSFULLY_UPLOADED_FILE,
                data: {
                  rows: [],
                  dStateId: null, // No upload needed
                  error: null,
                  success: true,
                  message: message,
                },
              })
            )
          }, 100)
          return
        }

        // Step 2: If validation passes, prepare for upload
        setWizardState((state) =>
          reducer(state, {
            action: Actions.UPDATE_FINAL_STEP,
            data: {
              error: null,
              loadingMessage: t(
                'create-chassis-dialog.bulk-upload-wizard.results-uploading-file-data'
              ),
            },
          })
        )

        // Step 3: Prepare CSV content with validated data
        const batchId = `batch_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`
        const csvLines = validationResponse.validatedRows.map((row) => {
          const nowDate = new Date().toISOString()
          return [
            escapeCsvField(row.operatorName),
            escapeCsvField(row.depotName),
            escapeCsvField(row.nChassis),
            escapeCsvField(row.nParc),
            escapeCsvField(row.location),
            row.vehicleTypeId, // Already resolved to ID by validation
            nowDate,
            nowDate,
            row.conformity,
            row.memberId, // Already resolved to ID by validation
            row.depotId, // Already resolved to ID by validation
            row.operatorId, // Already resolved to ID by validation
            null, // deletedAt
            batchId, // Unique batch identifier for control creation
          ].join('|')
        })

        const csvHeader =
          'operateur,depot,n_chassis,n_parc,location,vehicle_type_id,createdAt,updatedAt,conformity,member_id,depot_id,operator_id,deleted_at,bulk_batch_id'
        csvLines.unshift(csvHeader)
        const csvData = csvLines.join('\n')

        // Step 4: Upload file using chassis bulk upload endpoints
        const uploadDetails = await dataProvider.chassis.startBulkUpload()

        if (!uploadDetails.dStateId) {
          throw new Error(
            'Upload endpoint did not return a dStateId for job tracking'
          )
        }

        await dataProvider.chassis.performBulkUpload(uploadDetails, csvData)

        // Persist any auto-corrections to the memory table (fire-and-forget)
        const autoCorrections = (validationResponse.corrections || []).filter(
          (c) => c.type === 'auto'
        )
        if (autoCorrections.length > 0) {
          dataProvider.chassis
            .recordImportCorrections(autoCorrections)
            .catch((err) =>
              console.warn('Failed to record import corrections:', err)
            )
        }

        setWizardState((state) =>
          reducer(state, {
            action: Actions.SUCCESSFULLY_UPLOADED_FILE,
            data: {
              rows: validationResponse.validatedRows,
              dStateId: uploadDetails.dStateId,
              error: null,
            },
          })
        )
      } catch (error) {
        console.error('Chassis bulk upload error:', error)

        let errorMessage = t(
          'create-chassis-dialog.bulk-upload-wizard.results-upload-error'
        )

        // Handle specific axios errors
        if (error.message === 'Network Error') {
          errorMessage = t(
            'create-chassis-dialog.bulk-upload-wizard.results-network-error'
          )
        } else if (error.response && error.response.status) {
          // For HTTP status errors (404, 500, etc.), use the generic error message
          errorMessage = t(
            'create-chassis-dialog.bulk-upload-wizard.results-dstate-error'
          )
        }

        setWizardState((state) =>
          reducer(state, {
            action: Actions.ERROR_UPLOADING_FILE,
            data: {
              error: errorMessage,
            },
          })
        )
      }
    },
    [dataProvider, t]
  )

  // Function to escape and quote a CSV field if necessary
  function escapeCsvField(field) {
    if (field == null || field == undefined) return '""' // Handle null or undefined
    const strField = String(field) // Ensure the field is a string
    if (
      strField.includes('"') ||
      strField.includes('\n') ||
      strField.includes('|')
    ) {
      // Escape double quotes by doubling them
      const escaped = strField.replace(/"/g, '""')
      return `"${escaped}"` // Wrap the escaped content in double quotes
    }
    return strField // Return the field as is if no special handling needed
  }

  const onManualCorrection = useCallback(
    (field, originalValue, newValue) => {
      dataProvider.chassis
        .recordImportCorrections([
          { field, originalValue, correctedValue: newValue, type: 'manual' },
        ])
        .catch((err) =>
          console.warn('Failed to record manual correction:', err)
        )
    },
    [dataProvider]
  )

  const createPreviewFileStep = useCallback(
    () => (
      <PreviewFileStep
        workbookData={wizardState.workbook.data}
        details={wizardState.details}
        validationErrors={wizardState.validationErrors}
        corrections={wizardState.corrections}
        onConfirm={onFileImportClick}
        onManualCorrection={onManualCorrection}
      />
    ),
    [
      wizardState.workbook.data,
      wizardState.details,
      wizardState.validationErrors,
      wizardState.corrections,
      onFileImportClick,
      onManualCorrection,
    ]
  )

  const onResultConfirm = useCallback(() => {
    setWizardState(INITIAL_STATE)
    onCreated()
  }, [onCreated])
  const createResultStep = useCallback(() => {
    return (
      <ResultStep
        loadingMessage={wizardState.loadingMessage}
        dStateId={wizardState.dStateId}
        error={wizardState.error}
        success={wizardState.success}
        message={wizardState.message}
        onConfirm={onResultConfirm}
      />
    )
  }, [onResultConfirm, wizardState])

  const stepComponent = useMemo(() => {
    if (wizardState.step === Steps.importFile) {
      return createImportFileStep()
    }
    if (wizardState.step === Steps.previewFile) {
      return createPreviewFileStep()
    }
    if (wizardState.step === Steps.result) {
      return createResultStep()
    }
    throw new Error(`Illegal step: ${wizardState.step}`)
  }, [
    createImportFileStep,
    createPreviewFileStep,
    createResultStep,
    wizardState,
  ])

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      title={t('create-chassis-dialog.title')}
      width="md"
      hideActions
      classes={{ root: classes.noPadding }}
      disableEnforceFocus
    >
      <Stack direction="column" spacing={4} style={{ marginBottom: '1px' }}>
        <div className={classes.wizardHeader}>
          <WizardHeader
            numberOfSteps={3}
            selectedStep={wizardState.step}
            onStepClick={onStepClick}
          />
        </div>
        <div className={classes.stepPadding}>{stepComponent}</div>
      </Stack>
    </BaseDialog>
  )
}
